import { useRef, useState } from 'react'
import { Bell, DoorOpen, Loader2, MessageCircle, Play } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { PosServiceSlips } from '@/components/pos/PosServiceSlips'
import { printServiceSlips } from '@/components/pos/posServiceSlip'
import {
  checkInAppointment,
  notifyAppointmentStylist,
  startAppointmentInChair,
  triggerStylistReady,
} from '@/lib/api/appointments'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  canCheckInAppointment,
  canStartAppointmentInChair,
  checkInStatusLabel,
  defaultChairIdForAppointment,
  formatArrivalTime,
  canTriggerStylistReady,
  stylistReadyLabel,
} from '@/lib/pos/posFloor'
import { cn } from '@/lib/utils'

/**
 * Chair + check-in actions for one appointment (Calendar or POS).
 * @param {{
 *   appointment: import('@/types/api').Appointment
 *   chairs: import('@/types/api').Chair[]
 *   compact?: boolean
 *   disabled?: boolean
 *   onUpdated?: () => void | Promise<void>
 * }} props
 */
export function GuestArrivalActions({
  appointment,
  chairs,
  compact = false,
  disabled = false,
  onUpdated,
}) {
  const activeChairs = chairs.filter((chair) => chair.is_active !== false)
  const [busy, setBusy] = useState(false)
  const [chairId, setChairId] = useState(() =>
    String(defaultChairIdForAppointment(appointment) || ''),
  )
  const [error, setError] = useState('')
  const [serviceSlips, setServiceSlips] = useState(null)
  const serviceSlipRootRef = useRef(null)

  const showCheckIn = canCheckInAppointment(appointment)
  const showStart = canStartAppointmentInChair(appointment) && !showCheckIn
  const showStylistReady = canTriggerStylistReady(appointment)
  const stylistId = appointment.stylist_id ?? appointment.services?.[0]?.stylist_id
  const notifiedAt = stylistReadyLabel(appointment.stylist_ready_at)

  async function run(action) {
    setBusy(true)
    setError('')
    try {
      await action()
      await onUpdated?.()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to update guest status.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleCheckIn() {
    const payload = chairId ? { chair_id: Number(chairId) } : {}
    const result = await checkInAppointment(appointment.id, payload)
    if (result?.service_slips) {
      setServiceSlips(result.service_slips)
      window.setTimeout(() => printServiceSlips(serviceSlipRootRef.current), 100)
    }
  }

  async function handleCheckInAndStart() {
    if (!chairId) {
      setError('Pick a chair first.')
      return
    }
    await handleCheckIn()
    if (canStartAppointmentInChair({ ...appointment, status: 'checked_in' })) {
      await startAppointmentInChair(appointment.id)
    }
  }

  return (
    <div className={cn(compact ? 'space-y-2' : 'space-y-3')}>
      {!compact ? (
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-foreground">
              {appointment.customer?.name ?? 'Guest'}
            </p>
            <span className="rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {checkInStatusLabel(appointment.status)}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {formatArrivalTime(appointment.starts_at)}
            {appointment.stylist?.name ? ` · ${appointment.stylist.name}` : ''}
            {appointment.services?.[0]?.service?.name
              ? ` · ${appointment.services[0].service.name}`
              : ''}
            {notifiedAt ? ` · Notified ${notifiedAt}` : ''}
          </p>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          {formatArrivalTime(appointment.starts_at)}
          {appointment.services?.[0]?.service?.name
            ? ` · ${appointment.services[0].service.name}`
            : ''}
          {' · '}
          {checkInStatusLabel(appointment.status)}
        </p>
      )}

      {error ? <p className="text-[11px] text-destructive">{error}</p> : null}

      {activeChairs.length > 0 && (showCheckIn || showStart) ? (
        <select
          className={cn(
            'w-full rounded-md border border-input bg-background text-foreground',
            compact ? 'h-8 px-2 text-[11px]' : 'h-9 px-2 text-xs',
          )}
          value={chairId}
          disabled={busy || disabled}
          onChange={(event) => setChairId(event.target.value)}
          aria-label="Treatment chair"
        >
          <option value="">Select chair…</option>
          {activeChairs.map((chair) => (
            <option key={chair.id} value={chair.id}>
              {chair.name}
            </option>
          ))}
        </select>
      ) : null}

      <div className={cn('flex flex-wrap gap-1.5', compact && 'flex-col')}>
        {showCheckIn ? (
          <>
            <Button
              type="button"
              size="sm"
              className={cn(compact && 'h-8 w-full text-xs')}
              disabled={busy || disabled || !chairId}
              onClick={() => run(handleCheckInAndStart)}
            >
              {busy ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <DoorOpen className="mr-1.5 h-3.5 w-3.5" />
              )}
              Check in &amp; go to chair
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={cn(compact && 'h-8 w-full text-xs')}
              disabled={busy || disabled}
              onClick={() => run(handleCheckIn)}
            >
              Check in only
            </Button>
          </>
        ) : null}

        {showStart ? (
          <Button
            type="button"
            size="sm"
            className={cn(
              'bg-emerald-600 text-white hover:bg-emerald-700',
              compact && 'h-8 w-full text-xs',
            )}
            disabled={busy || disabled}
            onClick={() => run(() => startAppointmentInChair(appointment.id))}
          >
            {busy ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="mr-1.5 h-3.5 w-3.5" />
            )}
            Start service
          </Button>
        ) : null}

        {showStylistReady ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={cn(compact && 'h-8 w-full text-xs')}
            disabled={busy || disabled}
            onClick={() => run(() => triggerStylistReady(appointment.id))}
          >
            <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
            Stylist ready
          </Button>
        ) : null}

        {stylistId ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={cn(compact && 'h-8 w-full text-xs')}
            disabled={busy || disabled}
            onClick={() =>
              run(() => notifyAppointmentStylist(appointment.id, { stylist_id: stylistId }))
            }
          >
            <Bell className="mr-1.5 h-3.5 w-3.5" />
            Notify stylist
          </Button>
        ) : null}
      </div>

      {serviceSlips ? (
        <div ref={serviceSlipRootRef} className="sr-only">
          <PosServiceSlips document={serviceSlips} />
        </div>
      ) : null}
    </div>
  )
}
